import React, { useEffect, useState, useMemo } from 'react';
import { FormattedMessage, IntlProvider } from 'react-intl';
import FormattedNumber from 'bf-library/FormattedNumber';
import PropTypes from 'prop-types';
import handleOpenLink from '@kognitiv/bf-library/dist/esm/helpers';
import * as API from '../../services/API';
import { getInlineConfigFromQueue } from '../../helpers/WidgetConfigHelper';
import Card from 'Components/shared/Card';
import Button from 'Components/Card/Button';
import { useWidgetContext } from 'State/WidgetContext';
import { isTranslationAvailable } from 'Actions/translations';
import { QUERY_PARAMETERS } from 'Constants/QueryParameters';

/**
 *
 * This widget introduced the issue that the service which feeds it was being called before the widget config's been set.
 * Given the way the core layer (app.js) is done, and the required effort to fix so, very prone to bugs, it was decided to be fixed
 * using the someKeyNotSet value.
 */
function RewardProgram({ language, dispatch }) {
    const [ info, setInfo ] = useState({});

    const { widgetConfig, settingsConfig, translations } = useWidgetContext();

    const someKeyNotSet = useMemo(() => {
        // If there's at least one param set in the inline config but not yet at store, means I have to wait for
        // it to be set before calling service
        const inlineConfig = getInlineConfigFromQueue('rewardProgram');
        return Object.keys(inlineConfig).some(key => key !== 'id'
         && !!(inlineConfig[key]) && !(widgetConfig[key]));
    }, [ widgetConfig ]);

    const { currencyCode, actionUrl, fallbackAmount, layout, channelId } = widgetConfig;

    const { propertyCode, domain } = settingsConfig;

    const getProgramInfo = async () => {
        const sanitizedFallbackAmount = fallbackAmount || undefined;
        const newInfo = await API.getRewardInfo({ propertyCode, channelId, currencyCode, fallbackAmount: sanitizedFallbackAmount });
        setInfo(newInfo);
        return newInfo;
    };

    useEffect(() => {
        if (!someKeyNotSet) {
            getProgramInfo();
        }
    }, [ someKeyNotSet, widgetConfig ]);

    const {
        logo,
        noOffer,
        active,
        currencyDisplay,
        lowestRate = {},
        fallbackRate = {},
        channelId: backendChannelId,
    } = info;

    const { los } = lowestRate ?? {};

    const { reward, currency, total: currencyAmount = 0 } = noOffer ? fallbackRate : lowestRate;

    const { value: rewardsAmount, unit: rewardsUnit } = reward ?? {};

    const canRenderOffer = !!rewardsUnit && !!rewardsAmount;

    const finalChannelId = channelId || backendChannelId;

    const marketingLabelId = `hlrp.guest.widget.content.${finalChannelId}`;
    const mainTextAvailable = dispatch(isTranslationAvailable(marketingLabelId));
    const descriptionLabel = mainTextAvailable ? marketingLabelId : 'hlrp.guest.widget.content';

    const bookNowUrl = actionUrl || `https://${domain}?${QUERY_PARAMETERS.REWARD_PROGRAM}=${finalChannelId}&enable-mrp=true`;

    return (
        <IntlProvider locale={language} messages={translations}>
            <Card
                className={`kbe-reward-program-wrapper
            ${logo ? 'has-logo' : ''}
            layout-${layout}
            ${noOffer ? 'without-offer' : ''}`}
            >
                { logo ? (
                    <div className="kbe-reward-program-logo-wrapper">
                        <img src={logo} alt={finalChannelId} className="kbe-reward-program-logo" />
                    </div>
                ) : null }
                <div className="kbe-reward-program-description">
                    { noOffer ? <div className="kbe-reward-program-no-offer-title">
                        <FormattedMessage
                            id="hlrp.guest.widget.earnPerCurrency"
                            defaultMessage="hlrp.guest.widget.earnPerCurrency"
                            values={{
                                value         : rewardsAmount,
                                unit          : (<FormattedMessage
                                    id={`bmng.rewards.currency.${rewardsUnit}`}
                                    defaultMessage={`bmng.rewards.currency.${rewardsUnit}`}
                                />),
                                currencyValue : <FormattedNumber
                                    value={currencyAmount}
                                    currency={currency}
                                    fractionDigits={0}
                                    language={language}
                                    currencyDisplay={currencyDisplay}
                                />,
                            }}
                        />
                    </div> : null}
                    <div className="kbe-reward-program-no-offer-content">
                        <FormattedMessage
                            id={descriptionLabel}
                            defaultMessage={descriptionLabel}
                        />
                        { !active ? (
                            <div className="kbe-margin-top-small">
                                <FormattedMessage id="bf.subjectToRestrictions" defaultMessage="bf.subjectToRestrictions" />
                            </div>
                        ) : null }
                    </div>
                </div>
                { !noOffer && canRenderOffer ? (
                    <div className="kbe-reward-program-benefits-wrapper">
                        <div className="kbe-reward-program-benefit kbe-text-accent">
                            <FormattedMessage
                                id="bf.loyaltyProgramEarnedValue"
                                defaultMessage="bf.loyaltyProgramEarnedValue"
                                values={{
                                    value : rewardsAmount,
                                    unit  : (<FormattedMessage
                                        id={`bmng.rewards.currency.${rewardsUnit}`}
                                        defaultMessage={`bmng.rewards.currency.${rewardsUnit}`}
                                    />),
                                }}
                            />
                        </div>
                        <div className="kbe-reward-program-benefit-support-text">
                            <FormattedNumber
                                value={currencyAmount}
                                currency={currency}
                                fractionDigits={0}
                                language={language}
                                currencyDisplay={currencyDisplay}
                            />
                            {', '}
                            <FormattedMessage
                                id="bf.nightsCount"
                                defaultMessage="bf.nightsCount"
                                values={{ count: los }}
                            />
                        </div>
                    </div>
                ) : null }
                <Button
                    className="kbe-reward-program-selection-button"
                    content={<FormattedMessage id="bf.bookNow" defaultMessage="bf.bookNow" />}
                    onClick={() => handleOpenLink(bookNowUrl)}
                />
            </Card>
        </IntlProvider>
    );
}

RewardProgram.propTypes = {
    language : PropTypes.string.isRequired,
    dispatch : PropTypes.func.isRequired,
};

export default RewardProgram;

// i18nMultipleLabel[hlrp.guest.widget.content.*]
// i18nMultipleLabel[bmng.rewards.currency.*]
